/**
 * init.js — Widget Bootstrap
 *
 * PURPOSE:
 *   Entry point that runs once when the widget loads on a page.
 *   Coordinates all other modules to assemble the widget.
 *
 * HOW IT WORKS:
 *   1. Read data-tenant from the script tag
 *   2. Fetch tenant config (stubbed — see config.js)
 *   3. Initialize RedConcierge widget with LauncherButton and ChatPanel
 *
 * BE DEPENDENCY:
 *   None — fully FE. Config is stubbed until BE delivers
 *   GET /api/v1/tenant/{tenant_id}/config/ (Phase 1.4).
 *
 * USAGE:
 *   Called automatically when widget script loads:
 *   import { initWidget } from './core/init.js';
 *   await initWidget();
 */

import { getTenantId, fetchTenantConfig } from './config.js';
import { getState, setState, setMessages } from './state.js';
import { getTenantId as getConfigTenantId } from './configAccessors.js';
import { applyTheme } from './theme.js';
import { loadSession, persistSession } from './session.js';
import { loadHistory, attachChatHistoryPersistence } from './chat-history.js';
import { APP_ENV, getCustomerId } from './env.js';
import { injectDocumentFonts } from './fonts.js';
import { LauncherButton } from '../components/LauncherButton.js';
import { ChatPanel } from '../components/ChatPanel.js';
import * as chatFeature from '../features/base/chat.js';
import * as chatHandler from '../features/base/chat-handler.js';
import * as wineDrawerFeature from '../features/base/wine-drawer.js';
import * as analyticsFeature from '../features/optional/analytics.js';
import * as searchBarFeature from '../features/optional/search-bar.js';
import { loadVariant } from '../variants/index.js';
import { registerBaseCards } from '../components/base/registerCards.js';
import variablesCSS from '../styles/variables.css';
// Shared rich-card component structure (var-driven). Injected after the base
// vars and before the per-variant CSS so variant vars + overrides win.
import cardsCSS from '../styles/cards.css';
// Per-winery tenant CSS, split out of variables.css. Concatenated AFTER the base
// vars + shared rich-card scaffold so each tenant's :host(.ws-tenant-X) overrides
// win. Order matters only for valle/hestan (they override the shared scaffold);
// all tenants are mutually exclusive so the rest is order-independent.
import { TENANT_CSS } from '../styles/tenant-css.js';

export async function initWidget() {
  const tenantId = getTenantId();
  if (!tenantId && APP_ENV !== "production") {
    console.error(
      '[RedConcierge] Cannot initialize: no tenant ID found.'
    );
    return;
  }

  const config = await fetchTenantConfig(tenantId);

  // The backend decides whether this winery's concierge may run; the widget
  // only obeys. Render nothing at all — no launcher, no error, no branding.
  // A winery's billing state is never disclosed to its website visitors, so an
  // absent widget is the correct failure mode on a customer's public site.
  if (config.chatbot_enabled === false) {
    log('Concierge is unavailable for this tenant — widget not mounted.');
    return;
  }

  const emptyCartMessage = `Your ${config.tenant_id === 'thewineengine' ? 'Basket' : 'Cart'} is empty — add some wines to get started!`;
  setState({ tenant: config });
  setState({ cart: { ...getState().cart, emptyMessage: emptyCartMessage }});

  // Single source of truth for the logged-in shopper id: the host injects it on
  // the loader <script> (data-customer-id). account.js reads state.user.customerId.
  setState({ user: { ...getState().user, customerId: getCustomerId() } });

  // Boot URL params → session (parity: old script.js 171-198). tno=table no,
  // isQR=QR entry, openagent=force-open agent. Drives personalize_chat and the
  // is_QR guided-button gate. Parsed before loadVariant so getGuidedFlow sees it.
  const params = new URLSearchParams(window.location.search);
  setState({
    session: {
      ...getState().session,
      tableNo: params.get("tno") || getState().session.tableNo,
      isQR: params.get("isQR") === "true" || params.get("isQR") === "1",
      openagent: params.get("openagent") === "true",
      entryPoint: params.get("entry_point") || getState().session.entryPoint,
    },
  });

  // Rehydrate the prior conversation before anything renders. Use the config's
  // tenant_id (not the DOM data-tenant) so the keys match what chat-handler and
  // Header use at runtime — that's null in production. If a session was stored,
  // restore it AND its transcript so MessageList replays the messages instead
  // of re-seeding the welcome. Always attach persistence (the session may be
  // created later, on first panel open).
  const keyTenantId = getConfigTenantId();
  const storedSession = loadSession(keyTenantId);
  if (storedSession) {
    persistSession(keyTenantId, storedSession); // restore state.session.id
    const history = loadHistory(keyTenantId, storedSession);
    if (history.length) setMessages(history);
  }
  attachChatHistoryPersistence(keyTenantId);

  const container = document.createElement('div');
  container.id = 'redconcierge-widget';
  document.body.appendChild(container);

  const shadow = container.attachShadow({ mode: 'open' });
  if (!shadow) {
    console.error('[RedConcierge] Shadow DOM not supported');
    return;
  }

  // Web font faces go into the HOST PAGE's <head>, not the shadow root —
  // browsers ignore @font-face declared inside a shadow root. Must run before
  // applyTheme so the faces are requested as early as possible.
  injectDocumentFonts();

  injectSharedStyles(shadow);

  applyTheme(shadow);

  // Register base card renderers before the variant runs, so a variant can
  // override any card (e.g. Fogarty WineCard) by re-registering the name.
  registerBaseCards();

  await loadVariant({ config, shadow });

  const launcher = new LauncherButton(shadow);
  await launcher.init(config);

  const chatPanel = new ChatPanel(shadow);
  await chatPanel.init(config);

  await chatFeature.init(config);

  await chatHandler.init(config);

  wineDrawerFeature.init({ shadow });

  // Analytics + conversation-history tracking (parity with old getClientDetails
  // / trackHistory / shopNowBanner / calcTime / guided-flow counter). Listens
  // on the event bus — fire-and-forget; failures log via console.warn only.
  analyticsFeature.init();

  // Host-page search bar — only for a winery whose config defines `search_bar`
  // (no tenant has one by default). NOT awaited: it waits on the winery's own
  // markup to appear, which must never hold up the launcher/panel.
  searchBarFeature.init().catch((err) =>
    console.warn("[RedConcierge] search bar init failed", err),
  );

  // QR / ?openagent=true / ui_features.auto_open. Must run LAST, once every
  // ws-toggle listener above (panel, header, chat-handler, analytics) exists —
  // otherwise the open event is lost and the panel stays closed.
  launcher.maybeAutoOpen();

  const displayTenant = tenantId || "production";
  console.info(
    `[RedConcierge] Widget initialized for tenant: ${displayTenant}`
  );
}

function injectSharedStyles(shadow) {
  const style = document.createElement('style');
  style.textContent = variablesCSS + '\n' + cardsCSS + '\n' + TENANT_CSS;
  shadow.appendChild(style);
}
